import { Link } from 'react-router-dom';
import { 
  ArrowRight, Globe, Users, Award, Calendar, Target, Eye, Heart, 
  Building2, Briefcase, GraduationCap, Hotel, Cpu, CheckCircle,
  MapPin, Play, Shield, Star, TrendingUp, Zap, Clock, FileText
} from 'lucide-react';
import SEO from '../components/SEO';

// Hero Statistics
const heroStats = [
  { value: '10+', label: 'Years Experience', icon: Calendar },
  { value: '50+', label: 'Countries Served', icon: Globe },
  { value: '50,000+', label: 'Attendees Hosted', icon: Users },
  { value: '500+', label: 'Events Conducted', icon: Award },
];

// Core Values
const coreValues = [
  { title: 'Excellence', description: 'Delivering world-class events that exceed expectations' },
  { title: 'Integrity', description: 'Transparent operations and merit-based recognition' },
  { title: 'Innovation', description: 'Pioneering new formats and experiences' },
  { title: 'Global Impact', description: 'Connecting leaders across continents' },
];

// Event Portfolio Categories
const eventPortfolio = {
  exhibitions: {
    title: 'Global Exhibitions & Expos',
    icon: Building2,
    events: ['World AI Technology Expo', 'World Study Expo', 'Industry-specific Trade Exhibitions', 'International Business Expos'],
    stats: { visitors: '10,000+', exhibitors: '200+', countries: '30+' }
  },
  conferences: {
    title: 'Global Conferences & Summits',
    icon: Briefcase,
    events: ['AI Conferences', 'Entrepreneur & Investor Summit', 'Corporate Leadership Forums', 'Government & Public Sector Conferences'],
    stats: { delegates: '5,000+', speakers: '100+', sessions: '50+' }
  },
  awards: {
    title: 'International Awards Programs',
    icon: Award,
    events: ['CEO Awards', 'GM Awards', 'Hospitality Awards', 'Women Leadership Awards', 'Entrepreneur Awards', 'Industry Excellence Awards'],
    stats: { nominations: '5,000+', winners: '2,000+', industries: '20+' }
  },
  networking: {
    title: 'Networking & Private Events',
    icon: Users,
    events: ['VIP Networking Dinners', 'Yacht Networking Events', 'Investor Meetups', 'C-Level Roundtables'],
    stats: { connections: '10,000+', deals: '500+', partnerships: '200+' }
  }
};

// Timeline milestones
const timeline = [
  { year: '2014', title: 'Company Founded', description: 'Golden Tree Events established in Dubai, UAE' },
  { year: '2015', title: 'First Major Event', description: 'Launched International Travel Awards' },
  { year: '2017', title: 'International Expansion', description: 'Events across Middle East & Asia' },
  { year: '2019', title: '1,000+ Attendees', description: 'First mega-scale gala ceremony' },
  { year: '2021', title: 'Digital Transformation', description: 'Hybrid event capabilities launched' },
  { year: '2023', title: 'Global Awards Platform', description: '18+ award programs worldwide' },
  { year: '2024', title: 'AI & Tech Focus', description: 'World AI Expo & Tech Summit launched' },
];

// Regional Statistics
const regionalStats = [
  { region: 'Middle East', percentage: 45 },
  { region: 'Europe', percentage: 20 },
  { region: 'Asia Pacific', percentage: 18 },
  { region: 'Africa', percentage: 10 },
  { region: 'Americas', percentage: 5 },
  { region: 'Australia', percentage: 2 },
];

// Industry Breakdown
const industryStats = [
  { industry: 'Hospitality & Travel', percentage: 35, icon: Hotel },
  { industry: 'Technology & AI', percentage: 25, icon: Cpu },
  { industry: 'Education', percentage: 15, icon: GraduationCap },
  { industry: 'Corporate & Business', percentage: 15, icon: Briefcase },
  { industry: 'Government', percentage: 10, icon: Building2 },
];

// Event Process Steps
const processSteps = [
  { step: 1, title: 'Concept & Strategy', description: 'Custom event design aligned with your objectives' },
  { step: 2, title: 'Global Marketing', description: 'Delegate acquisition through targeted campaigns' },
  { step: 3, title: 'Sponsor Management', description: 'Premium sponsorship packages & exhibitor coordination' },
  { step: 4, title: 'Jury & Evaluation', description: 'Multi-level merit-based selection process' },
  { step: 5, title: 'Event Execution', description: 'World-class on-ground operations' },
  { step: 6, title: 'Post-Event PR', description: 'International media coverage & distribution' },
];

// Why Choose Us
const differentiators = [
  { title: 'Dubai Global Hub', description: 'Strategic location connecting East & West' },
  { title: '100% Merit-Based', description: 'No paid winners - pure excellence recognition' },
  { title: 'Strong Jury System', description: 'Industry experts ensuring credibility' },
  { title: 'Premium Venues', description: '5-star hotels & iconic locations' },
  { title: 'Global Media Reach', description: 'International PR distribution' },
  { title: 'Investor Network', description: 'Direct access to funding opportunities' },
];

// Leadership Team
const leadership = [
  { name: 'Executive Leadership', role: 'Strategic Direction', description: '20+ years combined experience in global event management' },
  { name: 'Operations Team', role: 'Event Excellence', description: 'Dedicated professionals ensuring flawless execution' },
  { name: 'Global Partners', role: 'International Reach', description: 'Regional representatives across 6 continents' },
];

// Social Impact
const socialImpact = [
  { title: 'Startup Support', description: 'Platform for emerging businesses to showcase innovations' },
  { title: 'Youth Empowerment', description: 'Student programs and educational initiatives' },
  { title: 'Women Leadership', description: 'Dedicated awards celebrating women achievers' },
  { title: 'Knowledge Sharing', description: 'Free webinars and industry insights' },
];

// Gallery Images
const galleryImages = [
  'https://customer-assets.emergentagent.com/job_3a8e81e0-2de4-4c76-829e-02137c446df7/artifacts/kqq8r3lj_Arabian-Best-of-Best-Awards.jpg',
  'https://customer-assets.emergentagent.com/job_3a8e81e0-2de4-4c76-829e-02137c446df7/artifacts/ktw2sp3a_world-ai-expo.jpg',
  'https://customer-assets.emergentagent.com/job_3a8e81e0-2de4-4c76-829e-02137c446df7/artifacts/6ztcpbij_europe-best-of-best-awards.jpg',
  'https://customer-assets.emergentagent.com/job_3a8e81e0-2de4-4c76-829e-02137c446df7/artifacts/bf45yk6j_World-Entrepreneur-Investor-Summit.jpg',
  'https://customer-assets.emergentagent.com/job_3a8e81e0-2de4-4c76-829e-02137c446df7/artifacts/5q7tt4x3_Golden-Women-Awards.jpg',
  'https://customer-assets.emergentagent.com/job_3a8e81e0-2de4-4c76-829e-02137c446df7/artifacts/08cyfp9t_World-CEO-Awards.jpg',
];

export const AboutPage = () => {
  return (
    <main data-testid="about-page" className="pt-24 min-h-screen bg-[#0a0a0a]">
      <SEO 
        title="About Us - Leading Event Management Company in Dubai"
        description="Learn about Golden Tree Events, Dubai's premier international event management company. 10+ years experience, 50+ countries served, 500+ events conducted. Exhibitions, summits, awards & networking."
        keywords="about Golden Tree Events, event management company Dubai, international events, awards management, exhibitions organizer UAE"
      />
      
      {/* 1. Hero Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/10 via-transparent to-[#D4AF37]/5" />
        <div className="w-full px-4 sm:px-8 lg:px-16 relative z-10">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <p className="font-google-sans text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">About Golden Tree Events</p>
            <h1 className="font-montserrat text-4xl sm:text-5xl lg:text-6xl text-white mb-6 leading-tight">
              Powering Global Events.<br />
              <span className="gradient-gold">Connecting Visionaries.</span><br />
              Creating Impact.
            </h1>
            <p className="font-google-sans text-[#A1A1AA] text-lg leading-relaxed">
              Golden Tree Events is a Dubai-based international event management company delivering world-class 
              exhibitions, summits, conferences, awards, and business networking platforms across multiple industries.
            </p>
          </div>

          {/* Hero Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {heroStats.map((stat, index) => (
              <div key={index} className="text-center p-6 bg-white/[0.02] border border-white/10 group hover:border-[#D4AF37]/50 transition-all">
                <stat.icon className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" />
                <span className="font-montserrat text-4xl text-[#D4AF37] block">{stat.value}</span>
                <p className="text-[#A1A1AA] text-sm mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 2. Who We Are */}
      <section className="py-16 bg-[#121212]">
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-google-sans text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">Who We Are</p>
              <h2 className="font-montserrat text-3xl sm:text-4xl text-white mb-6">
                Global Event & Awards <span className="gradient-gold">Management Company</span>
              </h2>
              <p className="font-google-sans text-[#A1A1AA] text-base leading-relaxed mb-6">
                Founded in Dubai, UAE, Golden Tree Events has established itself as a leading global event and 
                awards management company. We specialize in Technology, AI, Education, Entrepreneurship, 
                Hospitality, and Corporate Leadership sectors, delivering premium networking ecosystems 
                that connect industry leaders worldwide.
              </p>
              
              {/* Vision, Mission, Values */}
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-white/[0.02] border border-white/10">
                  <Target className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-montserrat text-white font-medium mb-1">Our Vision</h4>
                    <p className="text-[#A1A1AA] text-sm">To be the world's most trusted platform for business excellence recognition and global networking.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-white/[0.02] border border-white/10">
                  <Eye className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-montserrat text-white font-medium mb-1">Our Mission</h4>
                    <p className="text-[#A1A1AA] text-sm">Connecting visionaries, celebrating excellence, and creating lasting business impact through world-class events.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Core Values */}
            <div className="grid grid-cols-2 gap-4">
              {coreValues.map((value, index) => (
                <div key={index} className="p-6 bg-white/[0.02] border border-white/10 hover:border-[#D4AF37]/30 transition-all">
                  <Heart className="w-6 h-6 text-[#D4AF37] mb-4" />
                  <h4 className="font-montserrat text-white font-medium mb-2">{value.title}</h4>
                  <p className="text-[#A1A1AA] text-sm">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 3. Event Portfolio - MAJOR SECTION */}
      <section className="py-20">
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="text-center mb-16">
            <p className="font-google-sans text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">Our Portfolio</p>
            <h2 className="font-montserrat text-3xl sm:text-4xl text-white mb-4">
              Comprehensive <span className="gradient-gold">Event Ecosystem</span>
            </h2>
            <p className="font-google-sans text-[#A1A1AA] max-w-2xl mx-auto">
              From grand exhibitions to intimate networking dinners, we deliver diverse event formats tailored to your industry needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {Object.entries(eventPortfolio).map(([key, category]) => (
              <div key={key} className="p-8 bg-[#121212] border border-white/10 hover:border-[#D4AF37]/30 transition-all">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-[#D4AF37]/10 flex items-center justify-center">
                    <category.icon className="w-7 h-7 text-[#D4AF37]" />
                  </div>
                  <h3 className="font-montserrat text-white text-xl">{category.title}</h3>
                </div>
                
                <ul className="space-y-2 mb-6">
                  {category.events.map((event, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-[#A1A1AA]">
                      <CheckCircle className="w-4 h-4 text-[#D4AF37]" />
                      <span>{event}</span>
                    </li>
                  ))}
                </ul>

                <div className="grid grid-cols-3 gap-4 pt-6 border-t border-white/10">
                  {Object.entries(category.stats).map(([label, value]) => (
                    <div key={label} className="text-center">
                      <span className="text-[#D4AF37] font-bold block">{value}</span>
                      <span className="text-[#52525B] text-xs capitalize">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Global Statistics */}
      <section className="py-16 bg-[#121212]">
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="text-center mb-12">
            <p className="font-google-sans text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">Global Reach</p>
            <h2 className="font-montserrat text-3xl sm:text-4xl text-white">
              Regional <span className="gradient-gold">Client Statistics</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Regional Distribution */}
            <div className="p-8 bg-white/[0.02] border border-white/10">
              <h3 className="font-montserrat text-white text-xl mb-6">Clients by Region</h3>
              <div className="space-y-4">
                {regionalStats.map((item, index) => (
                  <div key={index}>
                    <div className="flex justify-between mb-1">
                      <span className="text-[#A1A1AA]">{item.region}</span>
                      <span className="text-[#D4AF37]">{item.percentage}%</span>
                    </div>
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-[#D4AF37] to-[#C5A028] rounded-full"
                        style={{ width: `${item.percentage}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Industry Breakdown */}
            <div className="p-8 bg-white/[0.02] border border-white/10">
              <h3 className="font-montserrat text-white text-xl mb-6">Industry Breakdown</h3>
              <div className="space-y-4">
                {industryStats.map((item, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between mb-1">
                        <span className="text-[#A1A1AA]">{item.industry}</span>
                        <span className="text-[#D4AF37]">{item.percentage}%</span>
                      </div>
                      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-[#D4AF37] to-[#C5A028] rounded-full"
                          style={{ width: `${item.percentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Key Numbers */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
            <div className="text-center p-6 border border-[#D4AF37]/30">
              <Globe className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
              <span className="font-montserrat text-3xl text-white block">50+</span>
              <p className="text-[#A1A1AA] text-sm">Countries Represented</p>
            </div>
            <div className="text-center p-6 border border-[#D4AF37]/30">
              <Users className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
              <span className="font-montserrat text-3xl text-white block">10,000+</span>
              <p className="text-[#A1A1AA] text-sm">Business Leaders Connected</p>
            </div>
            <div className="text-center p-6 border border-[#D4AF37]/30">
              <Award className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
              <span className="font-montserrat text-3xl text-white block">5,000+</span>
              <p className="text-[#A1A1AA] text-sm">Award Nominations</p>
            </div>
            <div className="text-center p-6 border border-[#D4AF37]/30">
              <Building2 className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
              <span className="font-montserrat text-3xl text-white block">1,000+</span>
              <p className="text-[#A1A1AA] text-sm">Sponsors & Exhibitors</p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. History Timeline */}
      <section className="py-16">
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="text-center mb-12">
            <p className="font-google-sans text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">Our Journey</p>
            <h2 className="font-montserrat text-3xl sm:text-4xl text-white">
              History & <span className="gradient-gold">Growth</span>
            </h2>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-[#D4AF37]/30" />
            
            <div className="space-y-8">
              {timeline.map((item, index) => (
                <div key={index} className={`relative flex items-center gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  {/* Timeline Dot */}
                  <div className="absolute left-4 md:left-1/2 w-4 h-4 bg-[#D4AF37] rounded-full transform -translate-x-1/2 z-10" />
                  
                  {/* Content */}
                  <div className={`ml-12 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                    <div className="p-6 bg-[#121212] border border-white/10">
                      <span className="text-[#D4AF37] font-bold text-xl">{item.year}</span>
                      <h4 className="font-montserrat text-white text-lg mt-2">{item.title}</h4>
                      <p className="text-[#A1A1AA] text-sm mt-1">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 6. Clients & Partners */}
      <section className="py-16 bg-[#121212]">
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="text-center mb-12">
            <p className="font-google-sans text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">Trusted Partners</p>
            <h2 className="font-montserrat text-3xl sm:text-4xl text-white mb-4">
              Our <span className="gradient-gold">Clients</span>
            </h2>
            <p className="font-google-sans text-[#A1A1AA] max-w-2xl mx-auto">
              Partnering with multinational corporations, government departments, universities, hospitality brands, 
              tech startups, SMEs, and industry associations worldwide.
            </p>
          </div>

          {/* Client Logos */}
          <div className="relative group mb-12">
            <div className="absolute -inset-1 bg-gradient-to-r from-[#D4AF37]/20 via-[#D4AF37]/5 to-[#D4AF37]/20 rounded-2xl blur-sm opacity-50" />
            <div className="relative bg-white rounded-2xl p-6">
              <img
                src="https://customer-assets.emergentagent.com/job_3a8e81e0-2de4-4c76-829e-02137c446df7/artifacts/4f3y1v78_clients-logo.webp"
                alt="Our Global Client Partners"
                className="w-full h-auto"
              />
            </div>
          </div>

          {/* Testimonials */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 bg-white/[0.02] border border-white/10">
              <p className="text-[#A1A1AA] italic mb-4">"Golden Tree Events delivered an exceptional awards ceremony that exceeded our expectations. Their attention to detail and professionalism is unmatched."</p>
              <p className="text-[#D4AF37] font-medium">— Regional Director, 5-Star Hotel Group</p>
            </div>
            <div className="p-6 bg-white/[0.02] border border-white/10">
              <p className="text-[#A1A1AA] italic mb-4">"The World AI Expo provided us with invaluable networking opportunities and helped us connect with key investors in the region."</p>
              <p className="text-[#D4AF37] font-medium">— CEO, Technology Startup</p>
            </div>
          </div>
        </div>
      </section>

      {/* 7. Event Process */}
      <section className="py-16">
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="text-center mb-12">
            <p className="font-google-sans text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">Our Methodology</p>
            <h2 className="font-montserrat text-3xl sm:text-4xl text-white">
              Professional <span className="gradient-gold">Event Process</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {processSteps.map((item) => (
              <div key={item.step} className="p-6 bg-[#121212] border border-white/10 hover:border-[#D4AF37]/30 transition-all group">
                <div className="w-12 h-12 bg-[#D4AF37] text-black font-bold text-xl flex items-center justify-center mb-4">
                  {item.step}
                </div>
                <h4 className="font-montserrat text-white text-lg mb-2">{item.title}</h4>
                <p className="text-[#A1A1AA] text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. Why Choose Us */}
      <section className="py-16 bg-[#121212]">
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="text-center mb-12">
            <p className="font-google-sans text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">Our Differentiators</p>
            <h2 className="font-montserrat text-3xl sm:text-4xl text-white">
              Why <span className="gradient-gold">Golden Tree Events?</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {differentiators.map((item, index) => (
              <div key={index} className="p-6 bg-white/[0.02] border border-white/10 hover:border-[#D4AF37]/30 transition-all">
                <Zap className="w-6 h-6 text-[#D4AF37] mb-4" />
                <h4 className="font-montserrat text-white text-lg mb-2">{item.title}</h4>
                <p className="text-[#A1A1AA] text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. Leadership */}
      <section className="py-16">
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="text-center mb-12">
            <p className="font-google-sans text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">Our Team</p>
            <h2 className="font-montserrat text-3xl sm:text-4xl text-white">
              Leadership <span className="gradient-gold">Team</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {leadership.map((member, index) => (
              <div key={index} className="p-8 bg-[#121212] border border-white/10 text-center">
                <div className="w-20 h-20 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-10 h-10 text-[#D4AF37]" />
                </div>
                <h4 className="font-montserrat text-white text-lg mb-1">{member.name}</h4>
                <p className="text-[#D4AF37] text-sm mb-3">{member.role}</p>
                <p className="text-[#A1A1AA] text-sm">{member.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 10. Awards Integrity */}
      <section className="py-16 bg-[#121212]">
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-google-sans text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">Our Standards</p>
              <h2 className="font-montserrat text-3xl sm:text-4xl text-white mb-6">
                Awards Integrity & <span className="gradient-gold">Compliance</span>
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <Shield className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-white font-medium">Transparent Selection Process</h4>
                    <p className="text-[#A1A1AA] text-sm">Clear criteria and scoring methodology</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Star className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-white font-medium">No Paid Winners Policy</h4>
                    <p className="text-[#A1A1AA] text-sm">100% merit-based recognition</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Users className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-white font-medium">Multi-Level Jury Review</h4>
                    <p className="text-[#A1A1AA] text-sm">Industry experts ensuring credibility</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <FileText className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-white font-medium">Governance Standards</h4>
                    <p className="text-[#A1A1AA] text-sm">Compliance with international best practices</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Impact */}
            <div className="p-8 bg-white/[0.02] border border-white/10">
              <h3 className="font-montserrat text-white text-xl mb-6">Social Impact & Community</h3>
              <div className="space-y-4">
                {socialImpact.map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <Heart className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-white font-medium">{item.title}</h4>
                      <p className="text-[#A1A1AA] text-sm">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 11. Gallery */}
      <section className="py-16">
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="text-center mb-12">
            <p className="font-google-sans text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">Event Highlights</p>
            <h2 className="font-montserrat text-3xl sm:text-4xl text-white">
              Venue & Event <span className="gradient-gold">Gallery</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {galleryImages.map((img, index) => (
              <div key={index} className="relative aspect-video overflow-hidden group">
                <img 
                  src={img} 
                  alt={`Event highlight ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-all" />
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/gallery" className="inline-flex items-center gap-2 text-[#D4AF37] font-medium hover:gap-3 transition-all">
              View Full Gallery <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* 12. Global Offices */}
      <section className="py-16 bg-[#121212]">
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="text-center mb-12">
            <p className="font-google-sans text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">Global Presence</p>
            <h2 className="font-montserrat text-3xl sm:text-4xl text-white">
              Our <span className="gradient-gold">Offices</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-6 bg-white/[0.02] border border-[#D4AF37]/30 text-center">
              <MapPin className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" />
              <h4 className="font-montserrat text-white text-lg mb-2">Dubai HQ</h4>
              <p className="text-[#A1A1AA] text-sm">
                9th Floor, MISA Business Center<br />
                Iris Bay, Business Bay<br />
                Dubai, UAE
              </p>
            </div>
            <div className="p-6 bg-white/[0.02] border border-white/10 text-center">
              <Globe className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" />
              <h4 className="font-montserrat text-white text-lg mb-2">International Partners</h4>
              <p className="text-[#A1A1AA] text-sm">
                Strategic partnerships across<br />
                Europe, Asia, Africa,<br />
                and the Americas
              </p>
            </div>
            <div className="p-6 bg-white/[0.02] border border-white/10 text-center">
              <Users className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" />
              <h4 className="font-montserrat text-white text-lg mb-2">Regional Representatives</h4>
              <p className="text-[#A1A1AA] text-sm">
                Local coordinators in<br />
                50+ countries for<br />
                seamless event delivery
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 13. CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#D4AF37]/10 via-[#D4AF37]/5 to-[#D4AF37]/10">
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="font-montserrat text-3xl sm:text-4xl text-white mb-4">
              Ready to <span className="gradient-gold">Partner With Us?</span>
            </h2>
            <p className="font-google-sans text-[#A1A1AA] mb-10">
              Join 1,000+ global brands who trust Golden Tree Events for their recognition and networking needs.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://goldentreeawards.com/award-nomination"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-medium px-8 py-4 uppercase tracking-widest text-sm hover:bg-[#C5A028] transition-all"
              >
                Nominate Today
                <ArrowRight className="w-4 h-4" />
              </a>
              <Link
                to="/contact-us"
                className="inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] font-medium px-8 py-4 uppercase tracking-widest text-sm hover:bg-[#D4AF37] hover:text-black transition-all"
              >
                Contact Us
              </Link>
              <Link
                to="/events"
                className="inline-flex items-center gap-2 border border-white/30 text-white font-medium px-8 py-4 uppercase tracking-widest text-sm hover:border-white hover:bg-white/5 transition-all"
              >
                View Events
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
